import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { EngagementManagerOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import { AttioEngagementManagerData, AttioEngagementService } from 'dashboard409a/services/attio/AttioEngagementService';
import { AttioTeamData } from 'dashboard409a/services/attio/AttioTeamService';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';

interface UseEngagementManagerProps {
  valuationRow: SimplifiedAttioValuation;
  onEngagementManagerUpdate?: (
    recordId: string,
    engagementManagerOption: EngagementManagerOption
  ) => Promise<void>;
}

const buildInitialManagers = (row: SimplifiedAttioValuation): AttioTeamData[] => {
  if (row.engagement_manager && row.engagement_manager !== '-' && row.engagement_manager_id) {
    return [{
      targetRecordId: row.engagement_manager_id,
      name: row.engagement_manager,
      imageSrc: row.engagement_manager_image_src || '',
    }];
  }
  return [];
};

export const useEngagementManager = ({ valuationRow, onEngagementManagerUpdate }: UseEngagementManagerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [engagementManagers, setEngagementManagers] = useState<AttioEngagementManagerData[]>([]);
  const [selectedManagers, setSelectedManagers] = useState<AttioTeamData[]>(() => buildInitialManagers(valuationRow));
  const [isDirty, setIsDirty] = useState(false);
  const engagementManagerService = useMemo(() => new AttioEngagementService(), []);
  const { enqueueSnackbar } = useSnackbar();

  const open = Boolean(anchorEl);
  const popoverId = open ? 'engagement-manager-popover' : undefined;

  // Re-sync selection from valuationRow each time the popover opens
  useEffect(() => {
    if (open) {
      setSelectedManagers(buildInitialManagers(valuationRow));
      setIsDirty(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateMutation = useMutation({
    mutationFn: ({
      valuationId,
      engagementManagerTargetRecordIds,
      snapshot,
    }: {
      valuationId: string;
      engagementManagerTargetRecordIds: string[];
      snapshot: AttioTeamData[];
    }) =>
      engagementManagerService.updateValuationEngagementManager({ valuationId, engagementManagerTargetRecordIds }),
    onSuccess: async (_, variables) => {
      if (onEngagementManagerUpdate) {
        const first = variables.snapshot[0] ?? null;
        await onEngagementManagerUpdate(variables.valuationId, {
          targetRecordId: first?.targetRecordId ?? '',
          name: first?.name ?? '-',
          imageSrc: first?.imageSrc ?? '-',
        });
      }
      enqueueSnackbar('Engagement manager updated successfully!', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to update engagement manager. Please try again.', { variant: 'error' });
    },
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Single-select: clicking the current selection clears it; clicking another replaces it.
  const handleToggleManager = (manager: AttioTeamData) => {
    setIsDirty(true);
    setSelectedManagers(prev =>
      prev.some(m => m.targetRecordId === manager.targetRecordId) ? [] : [manager]
    );
  };

  const handleClose = () => {
    const snapshot = [...selectedManagers];
    const dirty = isDirty;

    setAnchorEl(null);
    setSearchTerm('');
    setEngagementManagers([]);
    setIsDirty(false);

    if (dirty) {
      updateMutation.mutate({
        valuationId: valuationRow.record_id,
        engagementManagerTargetRecordIds: snapshot.map(m => m.targetRecordId),
        snapshot,
      });
    }
  };

  useEffect(() => {
    if (!searchTerm.trim()) {
      setEngagementManagers([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await engagementManagerService.searchEngagementManagers(searchTerm);
        setEngagementManagers(result);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm, engagementManagerService]);

  return {
    anchorEl,
    searchTerm,
    searching,
    engagementManagers,
    selectedManagers,
    open,
    popoverId,
    handleClick,
    handleClose,
    handleToggleManager,
    setSearchTerm,
    isUpdating: updateMutation.isPending,
  };
};
